import clsx from 'clsx';
import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { TabWithStarIcon } from 'src/components/Listings/InventoryEventPage/TabWithStarIcon';
import { Stack } from 'src/core/ui';
import { useUserHasAdvancedSearchV2Feature } from 'src/hooks/useUserHasFeature';
import { useUserHasConsignorMasterUI } from 'src/hooks/useUserIsConsignorMasterUI';
import { useServerUserSetting } from 'src/hooks/useUserSetting';
import { IconsFill, IconsProps } from 'src/svgs/Viagogo';
import { UserSetting } from 'src/WebApiController';

import * as styles from './MenuItem.css';

type MenuItemProps = {
  isActive?: boolean;
  textContent: React.ReactNode;
  path: string;
  icon: (props: IconsProps) => JSX.Element;
  onClick: (e: React.MouseEvent<HTMLDivElement>) => void;
  subRoutes?: Record<
    string,
    {
      Path: string;
      Icon: (props: IconsProps) => JSX.Element;
      CollapsedNavIcon?: (props: IconsProps) => JSX.Element;
    }
  >;
  favorite?: UserSetting;
  // POS-8591: thin/collapsed nav styling (feature 775)
  collapsedNav?: boolean;
};

export const MenuItem = ({
  isActive = false,
  textContent,
  icon: Icon,
  path,
  onClick,
  subRoutes,
  favorite,
  collapsedNav = false,
}: MenuItemProps) => {
  const [showContextMenu, setShowContextMenu] = useState(false);
  const handleContextMenu = (e: { preventDefault: () => void }) => {
    e.preventDefault(); // Prevent the default browser context menu
    setShowContextMenu((prev) => !prev);
  };

  const handleUnhover = () => {
    setShowContextMenu(false);
  };

  const isConsignorMasterView = useUserHasConsignorMasterUI();
  const hasSearchV2Feature = useUserHasAdvancedSearchV2Feature();

  const subRoutesEnabled = {
    Search: !isConsignorMasterView,
    SearchV2: !isConsignorMasterView && hasSearchV2Feature,
  } as Record<string, boolean>;

  const {
    value: favoriteMenuTabPath,
    setUserSetting: setFavoriteMenuTabPath,
    isLoading: favoriteMenuTabPathLoading,
  } = useServerUserSetting<string>({
    id: favorite,
  });

  const onSetDefaultMenuTabClick = useCallback(
    async (tab: string) => {
      setFavoriteMenuTabPath(tab === 'none' ? '' : tab);
    },
    [setFavoriteMenuTabPath]
  );

  return (
    <Stack
      className={clsx(styles.menuItemContainer, {
        [styles.expandedMenuItemContainer]: showContextMenu,
        [styles.expandedActiveMenuItemContainer]:
          !collapsedNav && showContextMenu && isActive,
      })}
      onClick={onClick}
      onContextMenu={handleContextMenu}
      direction="column"
      onMouseLeave={handleUnhover}
    >
      <Link
        to={favoriteMenuTabPath ? `${path}${favoriteMenuTabPath}` : path}
        className={
          collapsedNav
            ? clsx(styles.menuItem, { [styles.activeItem]: isActive })
            : clsx(styles.menuItemLegacy, {
                [styles.activeItemLegacy]: isActive,
              })
        }
      >
        <div
          className={clsx(styles.icon, {
            [styles.iconLegacy]: !collapsedNav,
          })}
        >
          <Icon
            size={collapsedNav ? '24px' : undefined}
            withHoverEffect
            fill={IconsFill.currentColor}
            isSelected={isActive}
          />
        </div>
        <span
          className={clsx(styles.menuText, {
            [styles.menuTextLegacy]: !collapsedNav,
          })}
        >
          {textContent}
        </span>
      </Link>
      {showContextMenu &&
        subRoutes &&
        Object.entries(subRoutes)
          .filter(([linkName]) => subRoutesEnabled[linkName])
          .map(([linkName, { Path, Icon: SubIcon, CollapsedNavIcon }]) => {
            const ResolvedSubIcon = collapsedNav
              ? (CollapsedNavIcon ?? SubIcon)
              : SubIcon;
            return (
              <Link
                key={linkName}
                to={`${path}${Path}`}
                className={
                  collapsedNav
                    ? styles.subMenuItem
                    : clsx(styles.menuItemLegacy, {
                        [styles.activeItemLegacy]: isActive,
                      })
                }
              >
                <div
                  className={clsx(styles.icon, {
                    [styles.iconLegacy]: !collapsedNav,
                  })}
                >
                  <ResolvedSubIcon
                    size={collapsedNav ? '24px' : undefined}
                    withHoverEffect
                    fill={IconsFill.currentColor}
                    isSelected={!collapsedNav && isActive}
                  />
                </div>
                <TabWithStarIcon
                  onIconClick={onSetDefaultMenuTabClick}
                  defaultTab={favoriteMenuTabPath}
                  tabType={Path}
                >
                  <span
                    className={clsx(styles.menuText, {
                      [styles.menuTextLegacy]: !collapsedNav,
                    })}
                  >
                    {linkName}
                  </span>
                </TabWithStarIcon>
              </Link>
            );
          })}
    </Stack>
  );
};
